// Bike component tracker — types + helpers. Persisted per account in Firestore
// (users/{uid}.bikes); no images uploaded, just pasted URLs.
import firebase from "firebase/app";
import "firebase/auth";

const PROXY =
  process.env.REACT_APP_PROXY_URL ?? "https://intervals-proxy.sebastiangranlund.workers.dev";

export interface PageMeta { title: string; brand: string; price: string; currency: string; image: string }

// Ask the worker to fetch a product page and return its metadata (OG/JSON-LD).
export const fetchPageMeta = async (url: string): Promise<PageMeta | null> => {
  const user = firebase.auth().currentUser;
  if (!user) return null;
  try {
    const token = await user.getIdToken();
    const res = await fetch(`${PROXY}/pagemeta?url=${encodeURIComponent(url)}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) return null;
    const d = await res.json();
    if (d?.error) return null;
    return { title: d.title || "", brand: d.brand || "", price: d.price || "", currency: d.currency || "", image: d.image || "" };
  } catch {
    return null;
  }
};

export interface Component {
  id: string;
  category: string; // e.g. "Kedja", "Kassett", "Framdäck"
  name: string; // model / description
  brand: string; // manufacturer (optional)
  size: string; // spec: tyre width, cassette range, crank length…
  weight: string; // e.g. "250 g"
  price: string; // e.g. "899 kr"
  link: string; // product / info URL
  image: string; // product image (auto-filled from the link)
  notes: string;
  date: string; // free-text installed/bought date, e.g. "2024" or "mars 2024"
  swapped: boolean; // true = replaced/upgraded from the original spec
  toBuy: boolean; // true = not owned yet, on the shopping list
}

export interface Bike {
  id: string;
  name: string;
  imageUrl: string;
  type: string; // e.g. "Landsväg", "MTB", "Gravel"
  notes: string;
  components: Component[];
}

// Suggested component categories (a store/build walk-through order).
export const COMPONENT_CATEGORIES = [
  "Ram",
  "Gaffel",
  "Bakdämpare",
  "Framhjul",
  "Bakhjul",
  "Framdäck",
  "Bakdäck",
  "Innerslang",
  "Kassett",
  "Kedja",
  "Vevparti",
  "Framväxel",
  "Bakväxel",
  "Växelreglage",
  "Bromsar fram",
  "Bromsar bak",
  "Bromsbelägg",
  "Bromsskivor",
  "Styre",
  "Stam",
  "Sadel",
  "Sadelstolpe",
  "Pedaler",
  "Vajrar/kablar",
  "Glidvalla",
  "Fästvalla",
  "Övrigt",
];

export const newId = (): string => Date.now().toString(36) + Math.random().toString(36).slice(2, 8);

export const blankBike = (): Bike => ({
  id: newId(),
  name: "",
  imageUrl: "",
  type: "",
  notes: "",
  components: [],
});

export const blankComponent = (category = ""): Component => ({
  id: newId(),
  category,
  name: "",
  brand: "",
  size: "",
  weight: "",
  price: "",
  link: "",
  image: "",
  notes: "",
  date: "",
  swapped: false,
  toBuy: false,
});

// The set of component slots a bike of a given type normally ships with, so a
// new bike can be pre-filled with the full checklist (empty brand/model, marked
// as original). The user then fills in details and flags what they've swapped.
const BASE_SLOTS = [
  "Ram", "Gaffel", "Framhjul", "Bakhjul", "Framdäck", "Bakdäck", "Kassett", "Kedja",
  "Vevparti", "Bakväxel", "Bromsar fram", "Bromsar bak", "Bromsbelägg",
  "Styre", "Stam", "Sadel", "Sadelstolpe", "Pedaler",
];
export const stockComponentSlots = (type: string): string[] => {
  const t = (type || "").toLowerCase();
  const slots = [...BASE_SLOTS];
  if (/mtb|berg|trail|enduro|downhill/.test(t)) slots.push("Framväxel", "Växelreglage", "Bromsskivor", "Bakdämpare");
  else if (/gravel|grus|cyclocross|cx/.test(t)) slots.push("Framväxel", "Växelreglage", "Bromsskivor");
  else if (/landsväg|road|race/.test(t)) slots.push("Framväxel", "Växelreglage");
  else slots.push("Framväxel", "Växelreglage");
  return slots;
};
export const stockComponents = (type: string): Component[] =>
  stockComponentSlots(type).map((cat) => blankComponent(cat));

// ---- Groupset presets: auto-fill the drivetrain/brakes with real model names ----
export interface Groupset {
  id: string;
  label: string;
  brand: string;
  type: "Landsväg" | "Gravel" | "MTB";
  parts: [string, string][]; // [category, model]
}

export const GROUPSETS: Groupset[] = [
  {
    id: "tiagra-4700", label: "Shimano Tiagra 4700 (10s)", brand: "Shimano", type: "Landsväg",
    parts: [["Växelreglage", "ST-4700"], ["Framväxel", "FD-4700"], ["Bakväxel", "RD-4700"], ["Vevparti", "FC-4700"], ["Kassett", "CS-HG500 (10s)"], ["Kedja", "CN-HG54"], ["Bromsar fram", "BR-4700"], ["Bromsar bak", "BR-4700"]],
  },
  {
    id: "105-r7000", label: "Shimano 105 R7000 (11s)", brand: "Shimano", type: "Landsväg",
    parts: [["Växelreglage", "ST-R7000"], ["Framväxel", "FD-R7000"], ["Bakväxel", "RD-R7000"], ["Vevparti", "FC-R7000"], ["Kassett", "CS-R7000 (11s)"], ["Kedja", "CN-HG601"], ["Bromsar fram", "BR-R7000"], ["Bromsar bak", "BR-R7000"]],
  },
  {
    id: "105-di2-r7100", label: "Shimano 105 Di2 R7100 (12s)", brand: "Shimano", type: "Landsväg",
    parts: [["Växelreglage", "ST-R7170"], ["Framväxel", "FD-R7150"], ["Bakväxel", "RD-R7150"], ["Vevparti", "FC-R7100"], ["Kassett", "CS-R7101 (12s)"], ["Kedja", "CN-M7100"], ["Bromsar fram", "BR-R7170"], ["Bromsar bak", "BR-R7170"]],
  },
  {
    id: "ultegra-r8000", label: "Shimano Ultegra R8000 (11s)", brand: "Shimano", type: "Landsväg",
    parts: [["Växelreglage", "ST-R8000"], ["Framväxel", "FD-R8000"], ["Bakväxel", "RD-R8000"], ["Vevparti", "FC-R8000"], ["Kassett", "CS-R8000 (11s)"], ["Kedja", "CN-HG701"], ["Bromsar fram", "BR-R8000"], ["Bromsar bak", "BR-R8000"]],
  },
  {
    id: "ultegra-di2-r8100", label: "Shimano Ultegra Di2 R8100 (12s)", brand: "Shimano", type: "Landsväg",
    parts: [["Växelreglage", "ST-R8170"], ["Framväxel", "FD-R8150"], ["Bakväxel", "RD-R8150"], ["Vevparti", "FC-R8100"], ["Kassett", "CS-R8100 (12s)"], ["Kedja", "CN-M8100"], ["Bromsar fram", "BR-R8170"], ["Bromsar bak", "BR-R8170"]],
  },
  {
    id: "dura-ace-r9200", label: "Shimano Dura-Ace Di2 R9200 (12s)", brand: "Shimano", type: "Landsväg",
    parts: [["Växelreglage", "ST-R9270"], ["Framväxel", "FD-R9250"], ["Bakväxel", "RD-R9250"], ["Vevparti", "FC-R9200"], ["Kassett", "CS-R9200 (12s)"], ["Kedja", "CN-M9100"], ["Bromsar fram", "BR-R9270"], ["Bromsar bak", "BR-R9270"]],
  },
  {
    id: "rival-axs", label: "SRAM Rival eTap AXS (12s)", brand: "SRAM", type: "Landsväg",
    parts: [["Växelreglage", "Rival eTap AXS"], ["Framväxel", "Rival eTap AXS"], ["Bakväxel", "Rival eTap AXS"], ["Vevparti", "Rival"], ["Kassett", "XG-1250 (12s)"], ["Kedja", "Rival"], ["Bromsar fram", "Rival hydraulisk"], ["Bromsar bak", "Rival hydraulisk"]],
  },
  {
    id: "force-axs", label: "SRAM Force eTap AXS (12s)", brand: "SRAM", type: "Landsväg",
    parts: [["Växelreglage", "Force eTap AXS"], ["Framväxel", "Force eTap AXS"], ["Bakväxel", "Force eTap AXS"], ["Vevparti", "Force"], ["Kassett", "XG-1270 (12s)"], ["Kedja", "Force"], ["Bromsar fram", "Force hydraulisk"], ["Bromsar bak", "Force hydraulisk"]],
  },
  {
    id: "grx-810", label: "Shimano GRX RX810 (11s)", brand: "Shimano", type: "Gravel",
    parts: [["Växelreglage", "ST-RX810"], ["Bakväxel", "RD-RX810"], ["Vevparti", "FC-RX810"], ["Kassett", "CS-HG700 (11s)"], ["Kedja", "CN-HG601"], ["Bromsar fram", "BR-RX810"], ["Bromsar bak", "BR-RX810"]],
  },
  {
    id: "grx-820", label: "Shimano GRX RX820 (12s)", brand: "Shimano", type: "Gravel",
    parts: [["Växelreglage", "ST-RX820"], ["Bakväxel", "RD-RX820"], ["Vevparti", "FC-RX820"], ["Kassett", "CS-M7100 (12s)"], ["Kedja", "CN-M7100"], ["Bromsar fram", "BR-RX820"], ["Bromsar bak", "BR-RX820"]],
  },
  {
    id: "rival-xplr-axs", label: "SRAM Rival XPLR AXS (12s)", brand: "SRAM", type: "Gravel",
    parts: [["Växelreglage", "Rival eTap AXS"], ["Bakväxel", "Rival XPLR eTap AXS"], ["Vevparti", "Rival Wide"], ["Kassett", "XG-1251 XPLR (10-44)"], ["Kedja", "Rival"], ["Bromsar fram", "Rival hydraulisk"], ["Bromsar bak", "Rival hydraulisk"]],
  },
  {
    id: "deore-m6000", label: "Shimano Deore M6000 (10s)", brand: "Shimano", type: "MTB",
    parts: [["Växelreglage", "SL-M6000"], ["Bakväxel", "RD-M6000"], ["Vevparti", "FC-M6000"], ["Kassett", "CS-HG500 (11-42, 10s)"], ["Kedja", "CN-HG54"], ["Bromsar fram", "BR-M6000"], ["Bromsar bak", "BR-M6000"], ["Bromsskivor", "RT56"]],
  },
  {
    id: "slx-m7000", label: "Shimano SLX M7000 (11s)", brand: "Shimano", type: "MTB",
    parts: [["Växelreglage", "SL-M7000"], ["Bakväxel", "RD-M7000"], ["Vevparti", "FC-M7000"], ["Kassett", "CS-M7000 (11-46, 11s)"], ["Kedja", "CN-HG601 (11s)"], ["Bromsar fram", "BR-M7000"], ["Bromsar bak", "BR-M7000"], ["Bromsskivor", "RT64"]],
  },
  {
    id: "xt-m8000", label: "Shimano XT M8000 (11s)", brand: "Shimano", type: "MTB",
    parts: [["Växelreglage", "SL-M8000"], ["Bakväxel", "RD-M8000"], ["Vevparti", "FC-M8000"], ["Kassett", "CS-M8000 (11-46, 11s)"], ["Kedja", "CN-HG701 (11s)"], ["Bromsar fram", "BR-M8000"], ["Bromsar bak", "BR-M8000"], ["Bromsskivor", "RT81"]],
  },
  {
    id: "xtr-m9000", label: "Shimano XTR M9000 (11s)", brand: "Shimano", type: "MTB",
    parts: [["Växelreglage", "SL-M9000"], ["Bakväxel", "RD-M9000"], ["Vevparti", "FC-M9000"], ["Kassett", "CS-M9000 (11-40, 11s)"], ["Kedja", "CN-HG901 (11s)"], ["Bromsar fram", "BR-M9000"], ["Bromsar bak", "BR-M9000"], ["Bromsskivor", "RT99"]],
  },
  {
    id: "sram-gx-1x11", label: "SRAM GX 1x11", brand: "SRAM", type: "MTB",
    parts: [["Växelreglage", "GX trigger (11s)"], ["Bakväxel", "GX 1x11"], ["Vevparti", "GX 1000"], ["Kassett", "PG-1130 (11-42, 11s)"], ["Kedja", "PC-1130"]],
  },
  {
    id: "deore-m6100", label: "Shimano Deore M6100 (12s)", brand: "Shimano", type: "MTB",
    parts: [["Växelreglage", "SL-M6100"], ["Bakväxel", "RD-M6100"], ["Vevparti", "FC-M6100"], ["Kassett", "CS-M6100 (10-51)"], ["Kedja", "CN-M6100"], ["Bromsar fram", "BR-M6100"], ["Bromsar bak", "BR-M6100"], ["Bromsskivor", "RT64"]],
  },
  {
    id: "slx-m7100", label: "Shimano SLX M7100 (12s)", brand: "Shimano", type: "MTB",
    parts: [["Växelreglage", "SL-M7100"], ["Bakväxel", "RD-M7100"], ["Vevparti", "FC-M7100"], ["Kassett", "CS-M7100 (10-51)"], ["Kedja", "CN-M7100"], ["Bromsar fram", "BR-M7100"], ["Bromsar bak", "BR-M7100"], ["Bromsskivor", "RT70"]],
  },
  {
    id: "xt-m8100", label: "Shimano XT M8100 (12s)", brand: "Shimano", type: "MTB",
    parts: [["Växelreglage", "SL-M8100"], ["Bakväxel", "RD-M8100"], ["Vevparti", "FC-M8100"], ["Kassett", "CS-M8100 (10-51)"], ["Kedja", "CN-M8100"], ["Bromsar fram", "BR-M8100"], ["Bromsar bak", "BR-M8100"], ["Bromsskivor", "RT86"]],
  },
  {
    id: "xtr-m9100", label: "Shimano XTR M9100 (12s)", brand: "Shimano", type: "MTB",
    parts: [["Växelreglage", "SL-M9100"], ["Bakväxel", "RD-M9100"], ["Vevparti", "FC-M9100"], ["Kassett", "CS-M9100 (10-51)"], ["Kedja", "CN-M9100"], ["Bromsar fram", "BR-M9100"], ["Bromsar bak", "BR-M9100"], ["Bromsskivor", "RT-MT900"]],
  },
  {
    id: "gx-eagle", label: "SRAM GX Eagle (12s)", brand: "SRAM", type: "MTB",
    parts: [["Växelreglage", "GX Eagle trigger"], ["Bakväxel", "GX Eagle"], ["Vevparti", "GX Eagle"], ["Kassett", "XG-1275 (10-52)"], ["Kedja", "GX Eagle"]],
  },
  {
    id: "nx-eagle", label: "SRAM NX Eagle (12s)", brand: "SRAM", type: "MTB",
    parts: [["Växelreglage", "NX Eagle trigger"], ["Bakväxel", "NX Eagle"], ["Vevparti", "NX Eagle"], ["Kassett", "PG-1230 (11-50)"], ["Kedja", "NX Eagle"]],
  },
  {
    id: "gx-eagle-axs", label: "SRAM GX Eagle AXS (12s)", brand: "SRAM", type: "MTB",
    parts: [["Växelreglage", "GX Eagle AXS controller"], ["Bakväxel", "GX Eagle AXS"], ["Vevparti", "GX Eagle"], ["Kassett", "XG-1275 (10-52)"], ["Kedja", "GX Eagle"]],
  },
];

const normalizeComponent = (c: any): Component => ({
  id: c?.id ?? newId(),
  category: c?.category ?? "Övrigt",
  name: c?.name ?? "",
  brand: c?.brand ?? "",
  size: c?.size ?? "",
  weight: c?.weight ?? "",
  price: c?.price ?? "",
  link: c?.link ?? "",
  image: c?.image ?? "",
  notes: c?.notes ?? "",
  date: c?.date ?? "",
  swapped: c?.swapped === true,
  toBuy: c?.toBuy === true,
});

export const normalizeBike = (b: any): Bike => ({
  id: b?.id ?? newId(),
  name: b?.name ?? "",
  imageUrl: b?.imageUrl ?? "",
  type: b?.type ?? "",
  notes: b?.notes ?? "",
  components: Array.isArray(b?.components) ? b.components.map(normalizeComponent) : [],
});

// Group a bike's components by category, in the suggested order (unknown
// categories appended alphabetically at the end).
export const groupComponents = (components: Component[]): { category: string; items: Component[] }[] => {
  const map = new Map<string, Component[]>();
  components.forEach((c) => {
    const key = c.category || "Övrigt";
    if (!map.has(key)) map.set(key, []);
    map.get(key)!.push(c);
  });
  const order = (cat: string) => {
    const i = COMPONENT_CATEGORIES.indexOf(cat);
    return i === -1 ? COMPONENT_CATEGORIES.length : i;
  };
  return Array.from(map.entries())
    .sort((a, b) => order(a[0]) - order(b[0]) || a[0].localeCompare(b[0], "sv"))
    .map(([category, items]) => ({ category, items }));
};

// ---- Valla inventory: seeded once as a ski-type pryl ----
export const VALLA_NAME = "Valla";
export const vallaBike = (): Bike => {
  const glide = (brand: string, name: string, link: string, extra: Partial<Component> = {}): Component =>
    ({ ...blankComponent("Glidvalla"), brand, name, link, ...extra });
  const skigo = (id: string) => `https://shopen.skigo.se/products/${id}`;
  const arctec = (slug: string) => `https://www.arctec.it/prodotto/${slug}/`;
  return {
    ...blankBike(),
    name: VALLA_NAME,
    type: "Längdskidor",
    notes: "",
    components: [
      glide("FFC", "LDQ 157 3.0 Glider", skigo("63040"), {
        size: "+2 till −20°C", price: "549 kr", notes: "Glidparaffin. Grunda med FFT Graphite, toppa med FFR.",
        image: "https://shopen.skigo.se/cdn/shop/files/63040-LDQ-157-30_FFC.jpg?v=1757104694",
      }),
      glide("FFR", "LDQ Pulver 3.0", skigo("62981"), {
        size: "+5 till −10°C", price: "999 kr", notes: "Toppningspulver.",
        image: "https://shopen.skigo.se/cdn/shop/files/LDQ_3_0_Powder.png?v=1757703098",
      }),
      glide("FFR", "Blue Powder", skigo("60656"), {
        size: "−1 till −10°C", price: "999 kr", notes: "Toppningspulver. Grunda med grafit + paraffin.",
        image: "https://shopen.skigo.se/cdn/shop/files/FFRBluepowder.jpg?v=1757947728",
      }),
      glide("Arctec", "RB Violet – Cold Racing Base", arctec("rb-violet-cold-racing-base")),
      glide("Arctec", "RB Red – Med Racing Base", arctec("rb-red-med-racin-base")),
      glide("Arctec", "RB Yellow – Hot Racing Base", arctec("rb-yellow-hot-racing-base")),
      glide("Arctec", "RB White – X Cold Racing Base", arctec("x-cold-racing-base")),
      { ...blankComponent("Fästvalla"), name: "Ingen vald ännu" },
    ],
  };
};
